import { useEffect, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Dialog, DialogActions, DialogContent, DialogTitle, Switch } from "@mui/material";
import { Grid, MapPin, Navigation, Send, Smartphone, Star, Truck, Users, Wind } from "react-feather";
import { authData } from "../../components/GetAuth";
import { formatGBP2, saveBilling } from "../../components/Features";
import billingStyles from "../../components/billing.module.css";
import styles from "./addons.module.css";

const ICONS = {
  route_optimization: <MapPin />,
  live_tracking: <Navigation />,
  membership: <Star />,
  referral: <Users />,
  driver_app: <Truck />,
  customer_app: <Smartphone />,
  air_freight: <Wind />,
  shipping_delivery: <Send />,
};

// Company admin turns add-ons on/off; the feature unlocks for the whole company immediately
const ServiceAddons = ({ showMessage, highlight }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [addons, setAddons] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const highlightRef = useRef(null);

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/billing/addons", {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setAddons(response.data.data);
        setSummary(response.data.summary);
        saveBilling(response.data.summary);
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [API_TOKEN]);

  // Arriving from a locked page (?enable=key) scrolls to that add-on
  useEffect(() => {
    if (loaded && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loaded]);

  const toggle = () => {
    const addon = confirm;
    const enable = !addon.enabled;
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + `v1/billing/addons/${addon.key}/${enable ? "enable" : "disable"}`, {}, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      setConfirm(null);
      if (response.data.status === "SUCCESS") {
        setAddons((prev) => prev.map((a) => a.key === addon.key ? { ...a, enabled: enable, enabled_at: enable ? dayjs().format() : null } : a));
        setSummary(response.data.data);
        saveBilling(response.data.data);
        showMessage(response.data.message);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      setConfirm(null);
      showMessage("Could not update the add-on. Please try again.", "error");
    });
  };

  const isAdmin = auth?.user_type === 2;
  const active = addons.filter((a) => a.enabled);
  const basic = Number(summary?.basic_price || 0);
  const basicList = Number(summary?.basic_list_price ?? basic);
  // Company price after any deal from the platform (free or discounted)
  const priceOf = (a) => Number(a.price ?? a.monthly_price ?? 0);
  const total = basic + active.reduce((sum, a) => sum + priceOf(a), 0);
  const inTrial = summary?.status === "trial";

  if (!loaded) return null;

  return (<div className={`${styles.Layout}`}>
    <div className={`${styles.AddonGrid}`}>
      {addons.map((addon) => {
        const isHighlight = highlight === addon.key;
        return (<div key={addon.key} ref={isHighlight ? highlightRef : null} className={`${styles.AddonCard} ${addon.enabled ? styles.AddonCardOn : ""} ${isHighlight && !addon.enabled ? styles.AddonCardHighlight : ""}`}>
          <div className={`${styles.AddonHead}`}>
            <span className={`${styles.AddonIcon}`}>{ICONS[addon.key] || <Grid />}</span>
            <div>
              <p className={`${styles.AddonName}`}>{addon.name}</p>
              <p className={`${styles.AddonPrice}`}>
                {priceOf(addon) !== Number(addon.list_price ?? addon.monthly_price) && <span className={`${styles.ListPrice}`}>{formatGBP2(addon.list_price ?? addon.monthly_price)}</span>}
                {addon.free ? "Free" : `${formatGBP2(priceOf(addon))}/month`}
                {addon.free && <span className={`${styles.DealBadge} ${styles.DealBadgeFree}`}>Included free</span>}
                {!addon.free && addon.discount_label && <span className={`${styles.DealBadge}`}>{addon.discount_label}</span>}
              </p>
            </div>
            <Switch
              className={`${styles.AddonSwitch}`}
              checked={!!addon.enabled}
              disabled={!isAdmin || (addon.free && addon.enabled)}
              onChange={() => setConfirm(addon)}
              inputProps={{ "aria-label": `${addon.enabled ? "Disable" : "Enable"} ${addon.name}` }}
            />
          </div>
          <p className={`${styles.AddonDesc}`}>{addon.description}</p>
          {addon.enabled && addon.enabled_at && <p className={`${styles.AddonMeta}`}>Active since {dayjs(addon.enabled_at).format("DD MMM YYYY")}</p>}
          {addon.free && addon.enabled && <p className={`${styles.AddonMeta}`}>Included free for your company, so it stays on.</p>}
        </div>);
      })}
    </div>

    <aside className={`${styles.Summary}`}>
      <p className={`${styles.SummaryTitle}`}>Monthly estimate</p>
      <div className={`${styles.SummaryLine}`}><span>Basic plan{summary?.basic_discount?.label ? ` (${summary.basic_discount.label})` : ""}</span><span>{basic !== basicList && <span className={`${styles.ListPrice}`}>{formatGBP2(basicList)}</span>}{formatGBP2(basic)}</span></div>
      {active.map((a) => (<div className={`${styles.SummaryLine}`} key={a.key}><span>{a.name}{a.free ? " (free)" : a.discount_label ? ` (${a.discount_label})` : ""}</span><span>{formatGBP2(priceOf(a))}</span></div>))}
      {active.length === 0 && <div className={`${styles.SummaryLine}`}><span>No add-ons yet</span><span>{formatGBP2(0)}</span></div>}
      <div className={`${styles.SummaryTotal}`}><span>Per month</span><strong>{formatGBP2(total)}</strong></div>

      <div className={`${styles.Timeline}`}>
        {inTrial && summary?.trial_ends_at && <div className={`${styles.TimelineRow}`}><span>Free trial ends</span><strong>{dayjs(summary.trial_ends_at).format("DD MMM YYYY")}</strong></div>}
        {summary?.next_invoice_date && <div className={`${styles.TimelineRow}`}><span>Next invoice emailed</span><strong>{dayjs(summary.next_invoice_date).format("DD MMM YYYY")}</strong></div>}
        {summary?.next_billing_date && <div className={`${styles.TimelineRow}`}><span>Payment due</span><strong>{dayjs(summary.next_billing_date).format("DD MMM YYYY")}</strong></div>}
      </div>

      <p className={`${styles.SummaryNote}`}>
        {inTrial ? "Add-ons are free during your trial. " : ""}
        Invoices are emailed 3 days before each billing date. Each add-on is charged for the full month in which it was active. Unpaid invoices have a {summary?.grace_period_days ?? 7}-day grace period after the due date, after which the service is paused until payment.
      </p>
    </aside>

    <Dialog open={!!confirm} onClose={() => !saving && setConfirm(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{confirm?.enabled ? "Turn off" : "Turn on"} {confirm?.name}?</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 14, lineHeight: "22px", color: "#4b5563", margin: 0 }}>
          {confirm?.enabled
            ? `${confirm?.name} will be switched off for everyone in your company straight away. It is still billed for the current month.`
            : `${confirm?.name} unlocks immediately for your whole company${inTrial ? ", free until your trial ends" : ""}. It adds ${formatGBP2(confirm ? priceOf(confirm) : 0)} per month to your bill.`}
        </p>
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px" }}>
        <button type="button" className={`${billingStyles.SecondaryBU}`} disabled={saving} onClick={() => setConfirm(null)}>Cancel</button>
        <button type="button" className={`${billingStyles.PrimaryBU}`} disabled={saving} onClick={toggle}>{saving ? "Saving…" : (confirm?.enabled ? "Turn off" : `Turn on · ${formatGBP2(confirm ? priceOf(confirm) : 0)}/mo`)}</button>
      </DialogActions>
    </Dialog>
  </div>);
}

export default ServiceAddons;
